/**
 * The volume groups a sound can be played on.
 *
 * `Sfx` and `Music` feed into `Master`, so muting the master silences everything while the two
 * gameplay buses keep their own levels. Three is deliberately all there is: a bus exists to be a knob
 * a player or a host can turn, and a knob nobody turns is dead weight.
 */
export const AudioBus = {
  /** Every other bus feeds into this one. */
  Master: 'master',
  /** Short gameplay sounds. */
  Sfx: 'sfx',
  /** Long looping tracks. */
  Music: 'music',
} as const;

/** One of the {@link AudioBus} groups. */
export type AudioBus = (typeof AudioBus)[keyof typeof AudioBus];

/**
 * One decoded sound, ready to play.
 *
 * Opaque in the same way {@link Texture} is: the backend decides what a clip really is, and the only
 * thing Kite promises about it is how long it lasts. Clips are produced by {@link AudioBackend.decode}
 * and owned by the asset layer.
 */
export interface AudioClip {
  /** Length of the sound in seconds. */
  readonly durationSeconds: number;
}

/** A sound that has started and can be stopped before it ends on its own. */
export interface AudioPlayback {
  /** Whether the sound is still running. */
  readonly isPlaying: boolean;
  /** Stops the sound immediately. Stopping an already finished playback does nothing. */
  stop(): void;
}

/** How one sound is played. Every field is resolved by {@link AudioManager} before it reaches here. */
export interface PlaybackRequest {
  /** Bus the sound's volume is scaled by. */
  readonly bus: AudioBus;
  /** Per-sound gain from zero to one, applied on top of the bus. */
  readonly volume: number;
  /** Whether the sound repeats until stopped. */
  readonly loop: boolean;
  /** Playback rate, where one is the recorded speed. */
  readonly rate: number;
}

/**
 * The engine's whole view of a sound backend.
 *
 * The same bargain the renderer strikes: one small interface, one real implementation talking to Web
 * Audio, and a silent implementation for anywhere that has none. Everything above this line deals in
 * clips, buses and volumes and never touches an `AudioContext`.
 *
 * Bus gains arrive already combined with mute state, so a backend stores what it is told rather than
 * reimplementing the volume policy.
 */
export interface AudioBackend {
  /** Whether the browser has let audio start. Nothing is audible before this turns true. */
  readonly isUnlocked: boolean;

  /** Starts audio output. Must be called from a user gesture; calling it twice is harmless. */
  unlock(): Promise<void>;

  /** Turns encoded bytes into a playable clip. */
  decode(bytes: ArrayBuffer): Promise<AudioClip>;

  /** Applies the effective gain of one bus, from zero to one. */
  setBusGain(bus: AudioBus, gain: number): void;

  /** Starts a clip and returns the handle that stops it. */
  play(clip: AudioClip, request: PlaybackRequest): AudioPlayback;

  /** Stops every sound currently playing. */
  stopAll(): void;

  /** Releases every backend resource. The backend must not be used afterwards. */
  dispose(): void;
}
